import { DropdownInlineEditor } from 'components/inline_edit/DropdownInlineEditor';
import { StatusLozenge } from 'pages/Settings/Integrations/StatusLozenge';
import { useCallback } from 'react';

type StatusLozengeOptions<TStatus extends string> = Readonly<{
    status: TStatus;
    color: string;
}>;

type StatusLozengeInlineEditorProps<
    TStatus extends string,
    TOption extends StatusLozengeOptions<TStatus>
> = Readonly<{
    currentStatus: TStatus;
    options: TOption[];
    isDisabled?: boolean;
    onSave: (updatedStatus: TOption | undefined) => void;
}>;

export function StatusLozengeInlineEditor<
    TStatus extends string,
    TOption extends StatusLozengeOptions<TStatus>
>({
    currentStatus,
    options,
    isDisabled,
    onSave,
}: StatusLozengeInlineEditorProps<TStatus, TOption>) {
    const getColor = useCallback(
        (item: TStatus | undefined) =>
            options.find((option) => option.status === item)?.color ?? '',
        [options]
    );
    const getIndex = useCallback(
        (item: TStatus | undefined) =>
            options.findIndex((option) => option.status === item),
        [options]
    );
    const maxWidth = Math.max(...options.map((option) => option.status.length)) * 1.5;
    return (
        <DropdownInlineEditor
            editorStyle={{ width: `${maxWidth}ch` }}
            renderValue={(value) => (
                <StatusLozenge status={value} color={getColor(value)} />
            )}
            value={currentStatus}
            options={options.map((option) => option.status)}
            onConfirm={(status) => {
                const idx = getIndex(status);
                onSave(idx === -1 ? undefined : options[idx]);
            }}
            dropdownConfig={{
                renderItem: ({ item }) => item,
                renderLabel: ({ value }) => value,
                dataKey: (item) => getIndex(item),
            }}
            isDisabled={isDisabled}
        />
    );
}
